import { useEffect, useRef } from 'react';
import { prepareNavigation, type NavigationRequest } from './navigation';

// Draft writes finish before app navigation. Browser reload/close uses the native
// unsaved-change warning because an asynchronous write cannot be guaranteed there.
export function useDraftNavigation(dirty: boolean, save: () => Promise<void>) {
  const state = useRef({ dirty, save });
  state.current = { dirty, save };
  useEffect(() => {
    const leaving = (event: Event) => {
      if (state.current.dirty)
        (event as CustomEvent<NavigationRequest>).detail.waitUntil(
          state.current.save()
        );
    };
    const unloading = (event: BeforeUnloadEvent) => {
      if (state.current.dirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    const link = (event: MouseEvent) => {
      if (
        !state.current.dirty ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const anchor =
        event.target instanceof Element ? event.target.closest('a') : null;
      if (!anchor || anchor.target || anchor.hasAttribute('download')) return;
      const destination = new URL(anchor.href, window.location.href);
      if (destination.origin !== window.location.origin) return;
      event.preventDefault();
      void prepareNavigation()
        .then(() => window.location.assign(destination.href))
        .catch(() => {
          /* The editor keeps the draft and displays the save error. */
        });
    };
    window.addEventListener('assist:before-navigation', leaving);
    window.addEventListener('beforeunload', unloading);
    document.addEventListener('click', link);
    return () => {
      window.removeEventListener('assist:before-navigation', leaving);
      window.removeEventListener('beforeunload', unloading);
      document.removeEventListener('click', link);
    };
  }, []);
}
